/** Patrol substate: pace between discovered waypoints with pauses, discover intersections for patrol circuits. */
import { distanceSq, worldToTile, tileToWorld, randomInRange, TAU } from '../../utils/Math';
import { TILE_SIZE } from '../../utils/Constants';
import { findPath } from '../../utils/Pathfinder';
import type { Position } from '../../utils/Types';
import type { PathHelper } from '../PathHelper';
import { MovementResult, ZERO_MOVEMENT, ALL_DIRS, PATROL_WAYPOINT_MIN_DIST_SQ, PATROL_WAYPOINT_MIN_POST_DIST_SQ, PATROL_PAUSE_BASE_MS, PATROL_PAUSE_RANDOM_MS, tileBlocksMovement } from '../constants';

/** Mutable patrol state owned by DutySystem. */
export interface PatrolState {
  waypoints: Position[];
  waypointIndex: number;
  path: Position[] | null;
  pathIndex: number;
  pauseTimer: number;
}

/** Create a fresh patrol state. */
export function createPatrolState(): PatrolState {
  return { waypoints: [], waypointIndex: 0, path: null, pathIndex: 0, pauseTimer: 0 };
}

/**
 * Advance the patrol substate: pace between discovered waypoints with pauses.
 * Returns movement result and whether patrol is exhausted (should transition to wander).
 */
export function patrolMovement(
  state: PatrolState,
  entityX: number, entityY: number,
  delta: number,
  moveSpeed: number,
  patrolDurationMs: number,
  substateTimer: { value: number },
  pathHelper: PathHelper,
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  logs: string[],
): { movement: MovementResult; exhausted: boolean } {
  // Timer-based transition
  if (patrolDurationMs > 0) {
    substateTimer.value += delta;
    if (substateTimer.value >= patrolDurationMs) {
      logs.push(`DUTY patrol → wander (${Math.round(substateTimer.value)}ms elapsed)`);
      substateTimer.value = 0;
      return { movement: ZERO_MOVEMENT, exhausted: true };
    }
  }
  // patrolDurationMs === -1 means patrol forever

  if (state.waypoints.length === 0) {
    logs.push('DUTY patrol → wander (no waypoints found)');
    return { movement: ZERO_MOVEMENT, exhausted: true };
  }

  // Pause at waypoint
  if (state.pauseTimer > 0) {
    state.pauseTimer -= delta;
    return { movement: ZERO_MOVEMENT, exhausted: false };
  }

  // Follow current patrol path
  if (state.path && state.path.length > 0) {
    const result = pathHelper.followWaypoints(state.path, state.pathIndex, entityX, entityY, moveSpeed);
    state.pathIndex = result.newIndex;

    if (!result.arrived) {
      return { movement: { vx: result.vx, vy: result.vy, wantsAttack: false }, exhausted: false };
    }

    // Arrived at waypoint — pause before advancing
    state.waypointIndex = (state.waypointIndex + 1) % state.waypoints.length;
    state.path = null;
    state.pauseTimer = randomInRange(PATROL_PAUSE_BASE_MS, PATROL_PAUSE_BASE_MS + PATROL_PAUSE_RANDOM_MS);
    return { movement: ZERO_MOVEMENT, exhausted: false };
  }

  // Need a new path to next waypoint
  const target = state.waypoints[state.waypointIndex];
  const { path } = findPath(groundLayer, entityX, entityY, target.x, target.y);
  if (path && path.length > 0) {
    state.path = path;
    state.pathIndex = 0;
  } else {
    // Can't reach waypoint — skip it
    state.waypointIndex = (state.waypointIndex + 1) % state.waypoints.length;
  }

  return { movement: ZERO_MOVEMENT, exhausted: false };
}

/**
 * Discover patrol waypoints near a duty post by scanning for intersections,
 * with sector-based fallback for even angular spread.
 */
export function discoverPatrolWaypoints(
  state: PatrolState,
  dutyX: number, dutyY: number,
  patrolRadius: number,
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  pathHelper: PathHelper,
): string[] {
  const logs: string[] = [];
  state.waypoints = [];
  state.waypointIndex = 0;
  state.path = null;

  const found: Position[] = [];
  const nearCandidates: Position[] = []; // intersections rejected as tooNear (for fallback)
  const radiusSq = patrolRadius * patrolRadius;
  let intersectionsChecked = 0;
  let tooClose = 0;
  let tooFar = 0;
  let tooNear = 0;

  // Minimum distance² for "near" intersections (1 tile) — used as fallback
  const NEAR_FALLBACK_DIST_SQ = (TILE_SIZE * 1) * (TILE_SIZE * 1);

  // Scan all 8 directions from the duty post for intersections
  for (const dir of ALL_DIRS) {
    const intersection = pathHelper.findNextIntersection(dutyX, dutyY, dir.dx, dir.dy);
    if (!intersection) continue;
    intersectionsChecked++;

    const dSq = distanceSq(dutyX, dutyY, intersection.x, intersection.y);

    if (dSq > radiusSq) { tooFar++; continue; }
    if (dSq < PATROL_WAYPOINT_MIN_POST_DIST_SQ) {
      tooNear++;
      // Keep near intersections as fallback candidates (if at least 1 tile away)
      if (dSq >= NEAR_FALLBACK_DIST_SQ) {
        const dup = nearCandidates.some(wp =>
          distanceSq(wp.x, wp.y, intersection.x, intersection.y) < PATROL_WAYPOINT_MIN_DIST_SQ
        );
        if (!dup) nearCandidates.push(intersection);
      }
      continue;
    }

    // Deduplicate
    const duplicate = found.some(wp =>
      distanceSq(wp.x, wp.y, intersection.x, intersection.y) < PATROL_WAYPOINT_MIN_DIST_SQ
    );
    if (duplicate) { tooClose++; continue; }

    found.push(intersection);
    if (found.length >= 4) break;
  }

  if (found.length >= 2) {
    state.waypoints = found;
    logs.push(`DUTY WAYPOINTS: ${found.length} via intersections (checked=${intersectionsChecked} tooFar=${tooFar} tooNear=${tooNear} dupes=${tooClose}) post=(${Math.round(dutyX)},${Math.round(dutyY)}) radius=${patrolRadius}`);
    return logs;
  }

  // If all intersections were tooNear, accept them at a lower threshold
  if (found.length < 2 && nearCandidates.length >= 2) {
    state.waypoints = nearCandidates.slice(0, 4);
    logs.push(`DUTY WAYPOINTS: ${state.waypoints.length} via near-fallback (checked=${intersectionsChecked} tooNear=${tooNear}) post=(${Math.round(dutyX)},${Math.round(dutyY)}) radius=${patrolRadius}`);
    return logs;
  }

  logs.push(`DUTY WAYPOINTS: intersections insufficient (found=${found.length} checked=${intersectionsChecked} tooFar=${tooFar} tooNear=${tooNear} dupes=${tooClose}) — trying sector fallback`);

  // Fallback: sector-based placement for angular spread
  const sectorCount = 4;
  const sectorSize = TAU / sectorCount;
  const sectorOffset = randomInRange(0, sectorSize);
  let sectorHits = 0;
  let sectorWalls = 0;

  for (let s = 0; s < sectorCount && state.waypoints.length < 4; s++) {
    const sectorMin = sectorOffset + s * sectorSize;
    const sectorMax = sectorMin + sectorSize;
    let foundInSector = false;

    for (let attempt = 0; attempt < 5; attempt++) {
      const angle = randomInRange(sectorMin, sectorMax);
      const dist = randomInRange(patrolRadius * 0.5, patrolRadius);
      const wx = dutyX + Math.cos(angle) * dist;
      const wy = dutyY + Math.sin(angle) * dist;
      const tx = worldToTile(wx);
      const ty = worldToTile(wy);
      const tile = groundLayer.getTileAt(tx, ty);
      if (!tileBlocksMovement(tile)) {
        const worldX = tileToWorld(tx);
        const worldY = tileToWorld(ty);

        const duplicate = state.waypoints.some(wp =>
          distanceSq(wp.x, wp.y, worldX, worldY) < PATROL_WAYPOINT_MIN_DIST_SQ
        );
        if (!duplicate) {
          state.waypoints.push({ x: worldX, y: worldY });
          sectorHits++;
          foundInSector = true;
          break;
        }
      } else {
        sectorWalls++;
      }
    }
    if (!foundInSector) {
      logs.push(`DUTY WAYPOINTS: sector ${s} failed (all attempts hit walls or dupes)`);
    }
  }

  logs.push(`DUTY WAYPOINTS: sector fallback → ${state.waypoints.length} waypoints (hits=${sectorHits} walls=${sectorWalls}) post=(${Math.round(dutyX)},${Math.round(dutyY)}) radius=${patrolRadius}`);
  return logs;
}
